import { useCallback, useEffect, useState } from 'react';

import type { PosCustomerBalanceSummary } from '../types';
import { clampCurrency, normalizeCurrency } from '../components/discountMath';

const parseInputValue = (value: string): number | null => {
  if (value.trim() === '') {
    return 0;
  }
  const normalized = value.replace(',', '.');
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

const describeBalanceError = (
  raw: number,
  creditAvailable: number,
): string | null => {
  // Solo validar que no supere el saldo disponible
  // Permitir cargar más que el total (para dejar saldo a favor)
  // Permitir cargar deuda sin restricciones (para pago parcial)
  if (raw > 0) {
    if (raw > creditAvailable + 0.0001) {
      return 'No puede superar el saldo disponible.';
    }
  }
  // No hay restricciones para saldo negativo (deuda)
  return null;
};

export interface UseBalanceApplicationOptions {
  total: number;
  balance?: PosCustomerBalanceSummary | null;
}

export interface UseBalanceApplicationResult {
  appliedAmount: number;
  appliedCredit: number;
  appliedDebt: number;
  draft: string;
  setDraft: (value: string) => void;
  reset: () => void;
  error: string | null;
  creditAvailable: number;
  debtAvailable: number;
  totalWithDebt: number;
  totalAfterBalance: number;
}

export const useBalanceApplication = (
  options: UseBalanceApplicationOptions,
): UseBalanceApplicationResult => {
  const { total, balance } = options;
  const baseTotal = clampCurrency(Math.max(total, 0));

  const creditAvailable = clampCurrency(Number(balance?.available_credit ?? 0));
  const debtAvailable = clampCurrency(Number(balance?.outstanding_debt ?? 0));

  const [draft, setDraftState] = useState('');
  const [appliedAmount, setAppliedAmount] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sanitizeAmount = useCallback(
    (raw: number): { value: number; message: string | null } => {
      if (!Number.isFinite(raw)) {
        return { value: 0, message: null };
      }

      if (raw === 0) {
        return { value: 0, message: null };
      }

      if (raw > 0) {
        // Aplicar crédito: solo limitar al saldo disponible
        // NO limitar al total de la venta (permite dejar saldo a favor)
        const message = describeBalanceError(raw, creditAvailable);
        const limited = Math.min(raw, creditAvailable);
        return { value: normalizeCurrency(limited, { allowNegative: false }), message };
      }

      // Aplicar deuda: sin restricciones
      // Permite pago parcial y cargar deuda libremente
      return { value: normalizeCurrency(raw, { allowNegative: true }), message: null };
    },
    [creditAvailable],
  );

  const computeDefault = useCallback((): number => {
    if (creditAvailable <= 0.009) {
      return 0;
    }
    if (baseTotal <= 0.009) {
      return 0;
    }
    const desired = Math.min(creditAvailable, baseTotal);
    return normalizeCurrency(desired, { allowNegative: false });
  }, [baseTotal, creditAvailable]);

  useEffect(() => {
    if (!isDirty) {
      const nextDefault = computeDefault();
      setAppliedAmount(nextDefault);
      setDraftState(nextDefault !== 0 ? nextDefault.toFixed(2) : '');
      setError(null);
      return;
    }

    setAppliedAmount((current) => {
      const { value, message } = sanitizeAmount(current);
      setError(message);
      setDraftState(value !== 0 ? value.toFixed(2) : '');
      return value;
    });
  }, [baseTotal, computeDefault, isDirty, sanitizeAmount]);

  const setDraft = useCallback(
    (value: string) => {
      setDraftState(value);
      const parsed = parseInputValue(value);
      if (parsed === null) {
        setError('Ingrese un monto válido.');
        return;
      }
      setIsDirty(true);
      const { value: sanitized, message } = sanitizeAmount(parsed);
      setAppliedAmount(sanitized);
      setError(message);
    },
    [sanitizeAmount],
  );

  const reset = useCallback(() => {
    setIsDirty(false);
  }, []);

  const appliedCredit = appliedAmount > 0 ? appliedAmount : 0;
  const appliedDebt = appliedAmount < 0 ? Math.abs(appliedAmount) : 0;
  const totalWithDebt = clampCurrency(baseTotal + appliedDebt);
  const totalAfterBalance = clampCurrency(Math.max(totalWithDebt - appliedCredit, 0));

  return {
    appliedAmount,
    appliedCredit,
    appliedDebt,
    draft,
    setDraft,
    reset,
    error,
    creditAvailable,
    debtAvailable,
    totalWithDebt,
    totalAfterBalance,
  };
};

export default useBalanceApplication;
